// S3 + STAC access for the Vantor Open Data bucket.
//
// Everything is read directly from the public bucket — there is no backend.
// We discover events from the *folder names* under `events/` using the S3
// ListObjectsV2 REST API (CORS is open for GET/HEAD), then read the STAC
// `collection.json` / item `*.json` documents for metadata.

export const BUCKET = 'https://vantor-opendata.s3.amazonaws.com';
export const ROOT_PREFIX = 'events/';

const XML = new DOMParser();

/**
 * List a single prefix via S3 ListObjectsV2, following continuation tokens.
 * Returns common prefixes (sub-"folders") and object keys.
 * @param {string} prefix
 * @param {string} [delimiter] pass '/' to get folder-style CommonPrefixes
 * @returns {Promise<{prefixes: string[], objects: {key: string, size: number}[]}>}
 */
export async function listPrefix(prefix, delimiter = '/') {
  const prefixes = [];
  const objects = [];
  let token = null;

  do {
    const url = new URL(BUCKET + '/');
    url.searchParams.set('list-type', '2');
    url.searchParams.set('prefix', prefix);
    if (delimiter) url.searchParams.set('delimiter', delimiter);
    if (token) url.searchParams.set('continuation-token', token);

    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`S3 list failed (${res.status}) for ${prefix}`);
    const doc = XML.parseFromString(await res.text(), 'application/xml');

    for (const cp of doc.querySelectorAll('CommonPrefixes > Prefix')) {
      prefixes.push(cp.textContent);
    }
    for (const c of doc.querySelectorAll('Contents')) {
      objects.push({
        key: c.querySelector('Key').textContent,
        size: Number(c.querySelector('Size')?.textContent || 0),
      });
    }

    const truncated = doc.querySelector('IsTruncated')?.textContent === 'true';
    token = truncated ? doc.querySelector('NextContinuationToken')?.textContent : null;
  } while (token);

  return { prefixes, objects };
}

const prettify = (id) => id.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Discover all events from the folder names under `events/`.
 * @returns {Promise<{id: string, name: string, prefix: string, collectionUrl: string}[]>}
 */
export async function listEvents() {
  const { prefixes } = await listPrefix(ROOT_PREFIX, '/');
  return prefixes
    .map((p) => {
      const id = p.replace(ROOT_PREFIX, '').replace(/\/$/, '');
      return {
        id,
        name: prettify(id),
        prefix: p,
        collectionUrl: `${BUCKET}/${p}collection.json`,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

async function fetchJson(url) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`Fetch failed (${res.status}): ${url}`);
  return res.json();
}

/**
 * Load one event: its STAC collection metadata plus every scene item.
 *
 * Scenes are discovered from the folder contents (every `*.tif` COG is paired
 * with a `<id>.json` STAC item). This is more robust than trusting the
 * collection's item links, which vary between events (some add pre/post
 * child catalogs).
 *
 * @param {{id: string, prefix: string, collectionUrl: string}} event
 * @returns {Promise<{collection: object|null, items: object[]}>}
 */
export async function loadEvent(event) {
  const [{ objects }, collection] = await Promise.all([
    listPrefix(event.prefix, '/'),
    fetchJson(event.collectionUrl).catch(() => null),
  ]);

  const tifs = objects.filter((o) => o.key.toLowerCase().endsWith('.tif'));
  const items = await Promise.all(
    tifs.map(async (o) => {
      const itemUrl = `${BUCKET}/${o.key.replace(/\.tif$/i, '.json')}`;
      try {
        const item = await fetchJson(itemUrl);
        return normalizeItem(item, o);
      } catch {
        return null; // a COG without a sidecar STAC item — skip it
      }
    })
  );

  return {
    collection,
    items: items.filter(Boolean).sort(sceneSort),
  };
}

// Sort: pre-event captures first, then by datetime.
function sceneSort(a, b) {
  const phaseRank = (p) => (p === 'pre' ? 0 : p === 'post' ? 2 : 1);
  const d = phaseRank(a.phase) - phaseRank(b.phase);
  if (d) return d;
  return (a.datetime || '').localeCompare(b.datetime || '');
}

/**
 * Pull the bits we care about out of a raw STAC item into a flat shape.
 */
function normalizeItem(item, obj) {
  const props = item.properties || {};
  const visual = item.assets?.visual || firstAssetOfType(item.assets, 'geotiff');
  const cogUrl = visual?.href || `${BUCKET}/${obj.key}`;
  const thumbAsset = item.assets?.thumbnail;
  const thumbUrl = thumbAsset?.href || cogUrl.replace(/\.tif$/i, '.jpg');

  return {
    id: item.id || obj.key.split('/').pop().replace(/\.tif$/i, ''),
    raw: item,
    cogUrl,
    thumbUrl,
    cogSize: obj.size,
    bbox: item.bbox,
    geometry: item.geometry,
    datetime: props.datetime,
    title: props.title || item.id,
    phase: props.phase,
    sensor: props.vehicle_name || props.platform,
    constellation: props.constellation,
    gsd: props.pan_gsd,
    cloud: props['eo:cloud_cover'],
    offNadir: props['view:off_nadir'],
    sunElev: props['view:sun_elevation'],
    properties: props,
  };
}

function firstAssetOfType(assets = {}, needle) {
  return Object.values(assets).find((a) => (a.type || '').includes(needle));
}
